import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  OnInit,
} from '@angular/core';
import { Subject } from 'rxjs';
import { ComputedFrom } from '@certemy/common';
import { MessageQueryService } from '@certemy/ui-components';
import { FeesStepInstance } from 'services/professional/step/step.interface';
import _get from 'lodash-es/get';
import _isFunction from 'lodash-es/isFunction';
import { getFullFeeStepAmount } from 'components/organization-certification/step/fee/fee-step.constants';
import { getFeeFormOptionsForPayment } from 'components/modal-windows/step-details/fees-details/fees-details.modal.component';
import { filter, switchMap, take } from 'rxjs/operators';
import { PaymentFormComponent, PaymentFormService } from 'features/payment-form';
import { getPaymentData } from 'features/payment-form/payment-form.constants';
import { STEP_TYPES } from 'constants/status.constants';
import { Selectors as ProfileSelectors } from 'features/profiles-state/+state/profiles-state.state';
import { Store } from '@ngrx/store';
import { CurrencyLocalePipe } from 'common/pipes/CurrencyLocalePipe';
import { PAYMENT_TYPES } from 'services/payment/payment.interface';
import { PAYMENT_FORM_PLACE } from 'features/payment-form/payment-form.constants';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

enum FeePaymentScreens {
  INIT,
  CONFIRM,
  ATTENTION,
}

@UntilDestroy()
@Component({
  selector: 'cm-fees-payment-screen',
  templateUrl: './fees-payment-screen.component.html',
  styleUrls: ['./fees-payment-screen.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FeesPaymentScreenComponent implements OnDestroy, OnInit {
  @Input() step: FeesStepInstance;
  @Output() closeModal = new EventEmitter();
  @Output() toggleMode = new EventEmitter();
  @Output() back = new EventEmitter();
  @Output() dismissModal = new EventEmitter();
  @ViewChild('paymentForm') paymentForm: PaymentFormComponent;

  readonly STEP_TYPES = STEP_TYPES;
  readonly PAYMENT_FORM_PLACE = PAYMENT_FORM_PLACE;
  screen = FeePaymentScreens.INIT;
  readonly FeePaymentScreens = FeePaymentScreens;

  @ComputedFrom('step')
  get paymentAmountFormatted() {
    const amount = new CurrencyLocalePipe(this.store).transform(this.paymentAmount);
    return `${this.currencyIcon}${amount}`;
  }

  @ComputedFrom('step')
  get paymentConfirmationInfo() {
    return {
      textFields: [
        {
          label: 'Step name:',
          text: this.step.name,
        },
        {
          label: 'Step type:',
          text: 'Fees',
        },
        {
          label: 'Payment amount:',
          text: this.paymentAmountFormatted,
        },
      ],
    };
  }

  @ComputedFrom('step')
  get endpointUrl() {
    return `/certification_step_instance/${this.step.step_instance_id}/pay`;
  }

  @ComputedFrom('step')
  get paymentAmount(): number {
    return getFullFeeStepAmount(this.step);
  }

  @ComputedFrom('step')
  get additionalPaymentParameters() {
    const feeFormOptions = _get(this.step.content, 'feesFormFields.length')
      ? getFeeFormOptionsForPayment(this.step.content.feesFormFields)
      : [];
    const paymentData = getPaymentData('optionInstances', feeFormOptions);
    const data = {
      [paymentData.key]: paymentData.data,
    };
    return JSON.stringify(data);
  }

  paymentFormData;
  onPay$ = new Subject<void>();
  currencyIcon: string;

  constructor(
    private paymentService: PaymentFormService,
    private messageQueryService: MessageQueryService,
    private store: Store<any>,
  ) {
    this.store
      .select(ProfileSelectors.paymentSymbol)
      .pipe(take(1))
      .subscribe((symbol) => (this.currencyIcon = symbol));
  }

  ngOnInit() {
    this.onPay$
      .pipe(
        switchMap(() => this.paymentForm.getData()),
        filter((data) => !!data),
        untilDestroyed(this),
      )
      .subscribe((data) => {
        this.paymentFormData = data;
        this.toggleMode.emit();
        const { showAttention, attentionCallback } = data;
        this.setScreen(showAttention && attentionCallback ? FeePaymentScreens.ATTENTION : FeePaymentScreens.CONFIRM);
      });
  }

  setScreen(page?: FeePaymentScreens) {
    this.screen = page || FeePaymentScreens.INIT;
  }

  ngOnDestroy() {}

  onPay() {
    this.onPay$.next();
  }

  attentionBack() {
    switch (this.paymentFormData.type) {
      case PAYMENT_TYPES.EPAY:
        this.setScreen(FeePaymentScreens.CONFIRM);
        break;
      default:
        this.toggleMode.emit();
        this.setScreen(FeePaymentScreens.INIT);
    }
  }

  attentionProceed() {
    this.paymentFormData.attentionCallback();
  }

  declineConfirmation() {
    this.toggleMode.emit();
    this.setScreen(FeePaymentScreens.INIT);
  }

  private _getUrlByType(type: PAYMENT_TYPES) {
    switch (type) {
      case PAYMENT_TYPES.EPAY:
      case PAYMENT_TYPES.EXACT:
        return '/payment/generate-prepayment-info';
      default:
        return `/certification_step_instance/${this.step.step_instance_id}/pay`;
    }
  }

  onConfirm() {
    switch (this.paymentFormData.type) {
      case PAYMENT_TYPES.EPAY:
      case PAYMENT_TYPES.EXACT:
        this.paymentFormData.attentionCallback = this.submitPayment.bind(this);
        this.setScreen(FeePaymentScreens.ATTENTION);
        break;
      default:
        this.submitPayment();
    }
  }

  submitPayment() {
    const feeFormOptions = _get(this.step.content, 'feesFormFields.length')
      ? getFeeFormOptionsForPayment(this.step.content.feesFormFields)
      : [];
    const paymentData = getPaymentData('optionInstances', feeFormOptions);
    const url = this._getUrlByType(this.paymentFormData.type);
    this.paymentService.onPay(url, this.paymentFormData, paymentData).subscribe(
      (response) => {
        if (_isFunction(this.paymentFormData.callback)) {
          this.paymentFormData.callback(response);
          return;
        }
        this.closeModal.emit();
        this.messageQueryService.addMessage(
          'Your payment was successful.<br>Payment confirmation is available in Digital Wallet!',
        );
      },
      (err) => {
        this.toggleMode.emit();
        this.setScreen(FeePaymentScreens.INIT);
        this.paymentForm.displayError([err]);
      },
    );
  }
}
